import React, {useContext, useEffect, useRef} from 'react';
import {PRINT_SIZE} from '../themes/Theme';
import {StateContext} from '../ContextProvider';

const TopoDraw = ({size}: {size: 'a4' | 'a6' | 'desktop' | 'mobile'}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const {xmlParsed} = useContext(StateContext);

    useEffect(() => {
        if (!xmlParsed) {
            return;
        }
        draw(xmlParsed);
    }, [xmlParsed]);

    const reducePoints = (distances: number[], elevations: number[]) => {
        let step = 20;
        if (distances.length < 1000) {
            step = 1;
        }

        let newDistances: number[] = [];
        let newElevations: number[] = [];

        for (let i = 0; i < distances.length; i += step) {
            newDistances.push(distances[i]);
            newElevations.push(elevations[i]);
        }

        return { newDistances, newElevations };
    };

    const draw = (xml: Document) => {
        const points = xml.getElementsByTagName("trkpt") as HTMLCollectionOf<Element>;

        if (!points) {
            return;
        }

        let distances = [];
        let elevations = [];
        let lastLat = null,
            lastLon = null,
            cumulativeDist = 0;

        for (let i = 0; i < points.length; i++) {
            if (points[i].getElementsByTagName("ele").length  === 0) {
                continue;
            }

            let lat = parseFloat(points[i].getAttribute("lat") ?? '');
            let lon = parseFloat(points[i].getAttribute("lon") ?? '');
            let ele = parseFloat(points[i].getElementsByTagName("ele")[0].textContent ?? '');

            if (lastLat !== null && lastLon !== null) {
                cumulativeDist += haversineDistance(lastLat, lastLon, lat, lon);
            }

            lastLat = lat;
            lastLon = lon;
            distances.push(cumulativeDist);
            elevations.push(ele);
        }

        const { newDistances, newElevations } = reducePoints(distances, elevations);
        drawElevationProfile(newDistances, newElevations);
    }

    const drawElevationProfile = (distances: number[], elevations: number[]) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        let minX = Math.min(...distances);
        let maxX = Math.max(...distances);
        let minY = Math.min(...elevations);
        let maxY = Math.max(...elevations);

        const scaleX = canvas.width / (maxX - minX);
        const scaleY = canvas.height / (maxY - minY);

        // 🎨 Début d'un seul grand chemin
        ctx.beginPath();
        ctx.moveTo(0, canvas.height); // Départ en bas à gauche

        for (let i = 0; i < distances.length; i++) {
            let x = (distances[i] - minX) * scaleX;
            let y = canvas.height - (elevations[i] - minY) * scaleY;
            ctx.lineTo(x, y);
        }

        ctx.lineTo(canvas.width, canvas.height); // Fermeture en bas à droite
        ctx.closePath();

        ctx.fillStyle = '#ffffff';
        ctx.fill(); // ✅ Remplit toute la zone sans trous
    };

    const haversineDistance = (lat1: any, lon1: any, lat2: any, lon2: any) => {
        const R = 6371e3; // Rayon de la Terre en mètres
        const toRad = (angle: any) => angle * (Math.PI / 180);
        let dLat = toRad(lat2 - lat1);
        let dLon = toRad(lon2 - lon1);
        let a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return (R * c) / 1000; // Retourne la distance en kilomètres
    };

    return (
        <div style={{...PRINT_SIZE[size].topo, position: 'absolute', left: 0, zIndex: 1, width: '100%'}}>
            <canvas ref={canvasRef} width={size === 'mobile' ? window.screen.width : PRINT_SIZE[size].canvaTopo.width}
                    height={PRINT_SIZE[size].canvaTopo.height}/>
        </div>
    )
};

export default TopoDraw;
